import { createApp } from "vue";
import "./style.css";
import App from "./App.vue";
import PrimeVue from "primevue/config";
import { VomiB2BPreset } from "./VomiB2BPreset.ts";
import Router from "./router";
import { createPinia } from "pinia";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import { initializeKeycloak } from "./security/keycloak.ts";
import { Capacitor } from "@capacitor/core";
import { useAuthStore } from "./stores/useAuthStore";
import { vClickOutside, vPinkElement } from "./directives";
import { initNativeUi } from "./services/native/nativeInit";
import { initPush } from "./services/push/PushService";
import { initDownloadNotifications } from "./services/native/downloadNotifications";
import { initLocale } from "./locales/useI18n";
import NativeLoginScreen from "./security/NativeLoginScreen.vue";

const isMockAuth = import.meta.env.VITE_MOCK_AUTH === "true";

const startApp = () => {
  if (!document.getElementById("app")) {
    document.body.innerHTML = '<div id="app"></div>';
  }
  const pinia = createPinia();
  const app = createApp(App);

  app
    .use(PrimeVue, {
      theme: {
        preset: VomiB2BPreset,
        options: {
          darkModeSelector: "none",
        },
      },
      // Date-component locale (month/day names) is synced to the active app language in App.vue.
    })
    .use(Router)
    .use(pinia)
    .use(ToastService)
    .use(ConfirmationService);

  app.directive('click-outside', vClickOutside);
  app.directive('pink-element', vPinkElement);

  app.mount("#app");

  if (isMockAuth) {
    const authStore = useAuthStore();
    authStore.setMockAuth({
      email: "test-user@vomi.com",
      firstName: "Test",
      lastName: "User",
      token: "mock-token-123",
    });
    console.log("🎭 Mock auth enabled - user auto-logged in");
  }

  void initNativeUi();
  void initPush();
  // Zatraži rano (uz push), ne prvi put tek kad korisnik nešto skine — isti OS permission
  // prompt kao push, pa se drugi put ionako ne pojavljuje ako je već odobren.
  void initDownloadNotifications();
};

// Mounted in place of the real app (before Router/Pinia exist) when native and there's no valid stored
// session. Its own tiny PrimeVue-themed app instance — no Router/Pinia needed for a login form. On
// success, unmount it and hand off to the normal boot path.
function showNativeLoginScreen() {
  if (!document.getElementById("app")) {
    document.body.innerHTML = '<div id="app"></div>';
  }
  const loginApp = createApp(NativeLoginScreen, {
    onSuccess: () => {
      loginApp.unmount();
      startApp();
    },
  });
  loginApp.use(PrimeVue, {
    theme: { preset: VomiB2BPreset, options: { darkModeSelector: "none" } },
  });
  loginApp.mount("#app");
}

if (isMockAuth) {
  console.log("🎭 Starting in MOCK AUTH mode (no Keycloak)");
  void initLocale().then(startApp);
} else {
  Promise.all([initializeKeycloak(), initLocale()])
    .then(([authenticated]) => {
      if (Capacitor.isNativePlatform() && !authenticated) {
        showNativeLoginScreen();
      } else {
        startApp();
      }
    })
    .catch((error) => {
      console.error("Keycloak initialization failed:", error);
      document.body.innerHTML = `
        <div style="padding: 2rem; font-family: sans-serif;">
          <h1>Authentication Error</h1>
          <p>Failed to initialize authentication. Please check:</p>
          <ul>
            <li>Keycloak URL: ${import.meta.env.VITE_KEYCLOAK_URL}</li>
            <li>Realm: ${import.meta.env.VITE_KEYCLOAK_REALM}</li>
            <li>Client ID: ${import.meta.env.VITE_KEYCLOAK_CLIENT_ID}</li>
          </ul>
          <p>Error: ${error}</p>
        </div>
      `;
    });
}
